import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { X } from 'react-feather';

/**
 * Glass-modal shell shared by every Settings form modal.
 *
 * Owns the backdrop, the close button, Esc-to-close, focus capture and
 * click-outside-to-close. Children render the form body + actions.
 *
 * Rendered through a portal to document.body because `.page-body` and
 * its CSSTransition wrapper apply a `transform` for route animations;
 * a `position: fixed` element nested inside that wrapper would be
 * positioned relative to the transformed ancestor (CSS spec quirk),
 * collapsing the backdrop to a tiny strip at the page-flow position
 * instead of covering the viewport.
 */
const ModalShell = ({ title, onClose, children, maxWidth = 640 }) => {
    const { t } = useTranslation();
    const backdropRef = useRef(null);
    const dialogRef = useRef(null);

    // Close on Esc.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Focus first input inside the modal on mount.
    useEffect(() => {
        const root = dialogRef.current;
        if (!root) return;
        const first = root.querySelector('input, select, textarea, button');
        if (first) first.focus();
    }, []);

    const handleBackdropMouseDown = (e) => {
        if (e.target === backdropRef.current) onClose?.();
    };

    const modal = (
        <div
            className="v3-sadmin-modal-backdrop"
            ref={backdropRef}
            onMouseDown={handleBackdropMouseDown}
        >
            <div className="v3-card v3-sadmin-modal" ref={dialogRef} style={{ maxWidth }}>
                <div className="v3-sadmin-modal-head">
                    <h2 className="v3-title">{title}</h2>
                    <button
                        type="button"
                        className="v3-sadmin-modal-x"
                        onClick={onClose}
                        aria-label={t('Close')}
                    >
                        <X size={16} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );

    if (typeof document === 'undefined') return modal;
    return createPortal(modal, document.body);
};

export default ModalShell;
